import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Wraps children and reveals them with a smooth animation when they enter the viewport.
 * Uses IntersectionObserver — no animation on mobile (respects the global CSS rule
 * that disables section-reveal transitions under 1024px).
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className] - extra classes on the wrapper
 * @param {number} [props.delay=0] - delay in ms before the reveal transition starts
 * @param {'up'|'left'|'right'|'fade'} [props.direction='up'] - entry direction
 */
export default function RevealOnScroll({ children, className, delay = 0, direction = 'up' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced-motion preference — show immediately
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hiddenTransform = {
    up: 'translateY(40px)',
    left: 'translateX(-40px)',
    right: 'translateX(40px)',
    fade: 'translateY(0)',
  };

  return (
    <div
      ref={ref}
      className={cn('transition-all duration-700 ease-out will-animate', className)}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : hiddenTransform[direction],
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}